import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from '../theme';
import { useLanguage } from '../lib/language';

// Helper to get text shadow based on theme
const getTextShadow = (theme: { mood: string }) => {
  const isLight = theme.mood === 'light';
  return isLight 
    ? '0 1px 2px rgba(255,255,255,0.35)' 
    : '0 1px 3px rgba(0,0,0,0.5), 0 2px 8px rgba(0,0,0,0.3)';
};

const Header: React.FC = () => {
  const { theme } = useTheme();
  const { language, setLanguage } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isRevealed, setIsRevealed] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const hasTriggeredFirstRevealRef = useRef(false);
  const location = useLocation();
  const textShadow = getTextShadow(theme);
  const isLight = theme.mood === 'light';

  useEffect(() => {
    const isHome = location.pathname === '/';

    // Keep navbar always visible outside Home.
    if (!isHome) {
      setIsRevealed(true);
      setIsScrolled((window.scrollY || 0) > 20);
      return;
    }

    // Before unlock: reveal only after first down movement.
    const REVEAL_SCROLL_Y = 72;
    const handleScroll = () => {
      const y = window.scrollY || 0;
      setIsScrolled(y > 50);
      setIsRevealed(y > REVEAL_SCROLL_Y);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [location.pathname]);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!isRevealed) setIsMobileMenuOpen(false);
  }, [isRevealed]);

  useEffect(() => {
    if (!isRevealed || hasTriggeredFirstRevealRef.current) return;
    hasTriggeredFirstRevealRef.current = true;
    window.dispatchEvent(new Event('header-first-reveal'));
  }, [isRevealed]);

  const navItems = [
    { label: language === 'es' ? 'Proyectos' : 'Work', path: '/works' },
    { label: language === 'es' ? 'Estudio' : 'Studio', path: '/studio' },
    { label: language === 'es' ? 'Contacto' : 'Contact', path: '/contact' },
  ];

  return (
    <>
      <motion.header
        initial={false}
        animate={isRevealed
          ? { opacity: 1, clipPath: 'inset(0% 0% 0% 0%)', y: 0 }
          : { opacity: 0, clipPath: 'inset(0% 0% 100% 0%)', y: -18 }}
        transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        className="fixed top-0 left-0 right-0 z-50 transition-all duration-500"
        style={{
          pointerEvents: isRevealed ? 'auto' : 'none',
          backgroundColor: isLight
            ? (isScrolled ? 'rgba(255, 255, 255, 0.72)' : 'rgba(255, 255, 255, 0.52)')
            : (isScrolled ? 'rgba(0, 0, 0, 0.72)' : 'rgba(0, 0, 0, 0.54)'),
          backdropFilter: 'blur(16px) saturate(160%)',
          WebkitBackdropFilter: 'blur(16px) saturate(160%)',
          borderBottom: isLight
            ? (isScrolled ? '1px solid rgba(0, 0, 0, 0.12)' : '1px solid rgba(0, 0, 0, 0.08)')
            : (isScrolled ? '1px solid rgba(255, 255, 255, 0.2)' : '1px solid rgba(255, 255, 255, 0.1)'),
          boxShadow: isLight ? '0 6px 20px rgba(0, 0, 0, 0.06)' : '0 2px 16px rgba(0, 0, 0, 0.4)',
        }}
      >
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-16">
          <div className="flex items-center justify-between h-16 sm:h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2 sm:gap-3 group">
              <div
                className="relative w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center overflow-hidden"
                style={{
                  border: isLight ? '1px solid rgba(0, 0, 0, 0.2)' : '1px solid rgba(255, 255, 255, 0.3)',
                  boxShadow: isLight ? '0 2px 8px rgba(0, 0, 0, 0.1)' : '0 2px 8px rgba(0, 0, 0, 0.3)',
                }}
              >
                <span
                  className="font-black text-base sm:text-lg tracking-tight"
                  style={{ 
                    color: 'var(--theme-text-primary, ' + theme.text.primary + ')',
                    textShadow,
                  }}
                >
                  M
                </span>
              </div>
              <span
                className="font-bold text-xs sm:text-sm tracking-[0.1em] uppercase hidden sm:block"
                style={{ 
                  color: 'var(--theme-text-primary, ' + theme.text.primary + ')',
                  textShadow,
                }}
              >
                Musashi
              </span>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-6 lg:gap-10">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className="relative group"
                >
                  <span
                    className="text-[11px] font-semibold uppercase tracking-[0.2em] transition-opacity"
                    style={{
                      color: 'var(--theme-text-primary, ' + theme.text.primary + ')',
                      textShadow,
                      opacity: location.pathname === item.path ? 1 : 0.7,
                    }}
                  >
                    {item.label}
                  </span>
                  {location.pathname === item.path && (
                    <motion.div
                      layoutId="nav-underline"
                      className="absolute -bottom-1 left-0 right-0 h-px"
                      style={{
                        backgroundColor: theme.text.primary,
                        boxShadow: isLight ? '0 0 8px rgba(0,0,0,0.2)' : '0 0 8px rgba(255,255,255,0.5)',
                      }}
                      transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                    />
                  )}
                </Link>
              ))}
            </nav>

            {/* CTA Button - Desktop */}
            <div className="flex items-center gap-2 sm:gap-4">
              <div
                className="hidden sm:flex items-center rounded-md border overflow-hidden"
                style={{ borderColor: isLight ? 'rgba(0,0,0,0.16)' : 'rgba(255,255,255,0.2)' }}
              >
                <button
                  type="button"
                  onClick={() => setLanguage('es')}
                  className="px-2.5 py-1.5 text-[10px] font-semibold tracking-[0.12em] uppercase transition-colors"
                  style={{
                    backgroundColor: language === 'es'
                      ? (isLight ? 'rgba(17,24,39,0.12)' : 'rgba(255,255,255,0.2)')
                      : 'transparent',
                    color: 'var(--theme-text-primary)',
                  }}
                >
                  ES
                </button>
                <button
                  type="button"
                  onClick={() => setLanguage('en')}
                  className="px-2.5 py-1.5 text-[10px] font-semibold tracking-[0.12em] uppercase transition-colors"
                  style={{
                    backgroundColor: language === 'en'
                      ? (isLight ? 'rgba(17,24,39,0.12)' : 'rgba(255,255,255,0.2)')
                      : 'transparent',
                    color: 'var(--theme-text-primary)',
                  }}
                >
                  EN
                </button>
              </div>

              <Link
                to="/contact"
                className="hidden md:inline-flex items-center gap-2 text-[10px] lg:text-[11px] font-semibold uppercase tracking-[0.1em] lg:tracking-[0.15em] px-3 lg:px-5 py-2 lg:py-2.5 transition-all"
                style={{
                  backgroundColor: 'var(--theme-btn-primary-bg)',
                  color: 'var(--theme-btn-primary-text)',
                }}
              >
                {language === 'es' ? 'Iniciar proyecto' : 'Start project'}
              </Link>

              {/* Mobile menu button */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="md:hidden relative w-10 h-10 flex items-center justify-center -mr-2"
                aria-label="Toggle menu"
              >
                <div className="relative w-5 h-3 flex flex-col justify-between">
                  <motion.span
                    className="w-full h-0.5 origin-center"
                    style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.25)', backgroundColor: theme.text.primary }}
                    animate={{
                      rotate: isMobileMenuOpen ? 45 : 0,
                      y: isMobileMenuOpen ? 5 : 0
                    }}
                    transition={{ duration: 0.2 }}
                  />
                  <motion.span
                    className="w-full h-0.5"
                    style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.25)', backgroundColor: theme.text.primary }}
                    animate={{ opacity: isMobileMenuOpen ? 0 : 1 }}
                    transition={{ duration: 0.2 }}
                  />
                  <motion.span
                    className="w-full h-0.5 origin-center"
                    style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.25)', backgroundColor: theme.text.primary }}
                    animate={{
                      rotate: isMobileMenuOpen ? -45 : 0,
                      y: isMobileMenuOpen ? -5 : 0
                    }}
                    transition={{ duration: 0.2 }}
                  />
                </div>
              </button>
            </div>
          </div>
        </div>
      </motion.header>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 md:hidden"
            style={{ backgroundColor: isLight ? 'rgba(255, 255, 255, 0.95)' : 'rgba(0, 0, 0, 0.95)' }}
          >
            <div className="flex flex-col items-center justify-center h-full gap-6 sm:gap-8 px-6">
              <div
                className="flex items-center rounded-md border overflow-hidden"
                style={{ borderColor: isLight ? 'rgba(0,0,0,0.16)' : 'rgba(255,255,255,0.2)' }}
              >
                <button
                  type="button"
                  onClick={() => setLanguage('es')}
                  className="px-3 py-2 text-xs font-semibold tracking-[0.12em] uppercase transition-colors"
                  style={{
                    backgroundColor: language === 'es'
                      ? (isLight ? 'rgba(17,24,39,0.12)' : 'rgba(255,255,255,0.2)')
                      : 'transparent',
                    color: 'var(--theme-text-primary)',
                  }}
                >
                  ES
                </button>
                <button
                  type="button"
                  onClick={() => setLanguage('en')}
                  className="px-3 py-2 text-xs font-semibold tracking-[0.12em] uppercase transition-colors"
                  style={{
                    backgroundColor: language === 'en'
                      ? (isLight ? 'rgba(17,24,39,0.12)' : 'rgba(255,255,255,0.2)')
                      : 'transparent',
                    color: 'var(--theme-text-primary)',
                  }}
                >
                  EN
                </button>
              </div>

              {navItems.map((item, idx) => (
                <motion.div
                  key={item.path}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 20 }}
                  transition={{ delay: idx * 0.1 }}
                >
                  <Link
                    to={item.path}
                    className="text-2xl sm:text-3xl font-bold uppercase tracking-wider transition-colors text-white"
                    style={{
                      color: theme.text.primary,
                      textShadow,
                      opacity: location.pathname === item.path ? 1 : 0.6,
                    }}
                  >
                    {item.label}
                  </Link>
                </motion.div>
              ))}

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 20 }}
                transition={{ delay: 0.3 }}
                className="mt-6 sm:mt-8"
              >
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.15em] px-6 sm:px-8 py-3 sm:py-4"
                  style={{ backgroundColor: 'var(--theme-btn-primary-bg)', color: 'var(--theme-btn-primary-text)' }}
                >
                  {language === 'es' ? 'Iniciar proyecto' : 'Start project'}
                </Link>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;
